import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

import { RecordType } from "../../../../../../../common/types/RecordType";
import { useAssociateFormContentReferenceMapper } from "../../../../../../common/content/hooks/useAssociateFormContentReferenceMapper";
import { CloudFileAssociationFormState } from "../../context/CloudFileAssociationContext";
import { useCloudFileAssociationSection } from "../../context/useCloudFileAssociationSection";
import { buildUpdateAudioCloudFileRequest } from "./request-builders/buildUpdateAudioCloudFileRequest";
import { buildUpdateSubtitleCloudFileRequest } from "./request-builders/buildUpdateSubtitleCloudFileRequest";
import { buildUpdateVideoCloudFileRequest } from "./request-builders/buildUpdateVideoCloudFileRequest";
import { useAssociateCloudFile } from "./useAssociateCloudFile";

export const useSaveCloudFileAssociationProgress = () => {
    const { cloudFileItem, selectedLanguages, selectedVideoTechnicalDetails } = useCloudFileAssociationSection();

    const { updateVideoCloudFile, updateAudioCloudFile, updateSubtitleCloudFile } = useAssociateCloudFile({
        invalidationKey: cloudFileItem?.id
    });

    const [isRequestPending, setIsRequestPending] = useState(false);

    const { getValues } = useFormContext<CloudFileAssociationFormState>();

    const { mapFormStateToContentReference } = useAssociateFormContentReferenceMapper();

    const saveCloudFile = useCallback(async () => {
        setIsRequestPending(true);

        const handler = await getHandler();

        try {
            return await handler();
        } finally {
            setIsRequestPending(false);
        }
    }, [selectedLanguages, selectedVideoTechnicalDetails]);

    const getHandler = async () => {
        if (!cloudFileItem) return async () => {};

        const contentRef = await mapFormStateToContentReference(getValues());

        const builderParams = {
            cloudFileId: cloudFileItem.id,
            contentRef,
            selectedLanguages,
            selectedVideoTechnicalDetails
        };

        switch (cloudFileItem.recordType) {
            case RecordType.Video:
                return async () => {
                    const req = buildUpdateVideoCloudFileRequest(builderParams);
                    return await updateVideoCloudFile.mutateAsync(req);
                };

            case RecordType.Audio:
                return async () => {
                    const req = buildUpdateAudioCloudFileRequest(builderParams);
                    return await updateAudioCloudFile.mutateAsync(req);
                };

            case RecordType.Subtitle:
                return async () => {
                    const req = buildUpdateSubtitleCloudFileRequest(builderParams);
                    return await updateSubtitleCloudFile.mutateAsync(req);
                };

            default:
                throw Error(`Unsupported RecordType: ${cloudFileItem.recordType}`);
        }
    };

    return {
        saveCloudFile,
        isRequestPending
    };
};
